import { createContext, useContext } from "react";
import type { FormLiveState } from "../../formLiveReducer";
import type { FormFlow } from "./formFlow";
import type {
  FormActionResult,
  FormValues,
  RequestFormOptionsInput,
} from "./formTypes";

/**
 * What the form-first cards need from the chat, without threading it through
 * `MessageList` and `MessageBubble` (which know nothing about forms).
 *
 * Provided once by `AgentChat`, from `useAgentChat`. The cards read the thread's
 * state (`flow`, derived from the persisted rows), the stream's (`live`), and call the
 * four actions.
 */
export type AgentFormContextValue = {
  sessionId: string;
  flow: FormFlow;
  live: FormLiveState;
  /** The session accepts this action on its current step (`availableActions`). */
  can: (action: string) => boolean;
  /**
   * A job is running. The backend refuses a second one, so anything that would queue
   * one (choosing a concept, regenerating, re-submitting the Brief) waits.
   */
  busy: boolean;
  /** A concept was chosen and its script is still being written. */
  waitingForScript: boolean;
  /** Submit a FORM card with the `action` it carries. */
  submitForm: (action: string, values: FormValues) => Promise<FormActionResult>;
  requestFormOptions: (input: RequestFormOptionsInput) => Promise<boolean>;
  selectConcept: (conceptId: string) => Promise<FormActionResult>;
  regenerateConcepts: () => Promise<FormActionResult>;
};

const AgentFormContext = createContext<AgentFormContextValue | null>(null);

export const AgentFormProvider = AgentFormContext.Provider;

export const useAgentForm = (): AgentFormContextValue => {
  const value = useContext(AgentFormContext);
  if (!value) {
    throw new Error("useAgentForm must be used inside an AgentFormProvider");
  }
  return value;
};
